import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { StandardButton } from 'components/ui/Button';
import { getStaticMediaUrl } from 'utils/Urls';
import arrowIcon from 'assets/icon--down-arrow-white.png';
import { PayrollMetaData, SkipTypes } from '../utils/types';
import { useModal } from 'components/ui/Modals';
import SkipPayroll from '../Popups/SkipPayroll';
import { useErrorMessageContext } from '../contexts/RunPayrollErrorContext';
import { UseIdsType } from '../utils/useIds';
import { trackXPayrollEvent } from 'utils/analytics';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';

import styles from './index.module.scss';

const SkipButton = ({
  selectedIds,
  activePayrollMeta,
  payrollMonth,
  totalActiveCount,
}: {
  selectedIds: UseIdsType | null;
  activePayrollMeta: PayrollMetaData[];
  payrollMonth: string;
  totalActiveCount: number;
}) => {
  const [isOptionsOpened, setIsOptionsOpened] = useState<boolean>(false);
  const { openModal } = useModal();
  const errCtx = useErrorMessageContext();
  const features = useSelector(loggedInUserSelectors.features);

  // Org-wide except-mode (send the ids to SPARE, backend skips the rest) is a
  // hrisCustomFields capability. With the flag OFF this component behaves
  // exactly as it did before that feature: the except options operate on the
  // unselected rows *currently in the grid* (filter-scoped) and are hidden when
  // they would be a no-op.
  const isExceptModeEnabled = Boolean(features?.hrisCustomFields);

  if (!selectedIds?.value.length) {
    return null;
  }

  const toggleDropdown = () => {
    setIsOptionsOpened((prev) => !prev);
  };

  const selectedList = activePayrollMeta.filter((meta) => selectedIds.value.includes(meta.id));

  const selectedIsCancelledList = selectedList.map((meta) => meta.isCancelled);

  const unselectedList = activePayrollMeta.filter((meta) => !selectedIds.value.includes(meta.id));

  const unselectedIsCancelledList = unselectedList.map((meta) => meta.isCancelled);

  const isAllSelectedSkipped = !selectedIsCancelledList.includes(false);
  const isAllSelectedUnskipped = !selectedIsCancelledList.includes(true);
  const isAllUnselectedSkipped = !unselectedIsCancelledList.includes(false);
  const isAllUnselectedUnskipped = !unselectedIsCancelledList.includes(true);

  // Flag ON: org-wide complement, counted from the backend's org-wide total.
  // Flag OFF: the grid's own unselected rows, exactly as before.
  const exceptCount = isExceptModeEnabled ? totalActiveCount - selectedIds.value.length : unselectedList.length;

  const canSkipAllExcept = isExceptModeEnabled ? exceptCount > 0 : !isAllUnselectedSkipped;
  const canResumeAllExcept = isExceptModeEnabled ? exceptCount > 0 : !isAllUnselectedUnskipped;

  const showDropdown =
    (!isAllSelectedSkipped && !isAllSelectedUnskipped) || canSkipAllExcept || canResumeAllExcept;

  const onSkipOrResume = () => {
    selectedIds.setValue([]);
  }

  const showConfirm = (type: string, exceptCase: boolean = false) => {
    trackXPayrollEvent(`runpayroll.${type === SkipTypes.SKIPPED ? 'skip_button_clicked' : 'resume_button_clicked'}`);
    const currentSelectedIds = selectedList.map((meta) => meta.id);
    const unselectedIds = unselectedList.map((meta) => meta.id);

    // Except-case, flag OFF: fall back to the legacy shape — an explicit list of
    // the unselected grid rows sent as skipIds. Only the flag-ON path uses
    // exceptIds (which makes the backend act org-wide).
    const useExceptIds = exceptCase && isExceptModeEnabled;
    const legacyExceptCase = exceptCase && !isExceptModeEnabled;

    openModal(
      <SkipPayroll
        payrollMonth={payrollMonth}
        type={type}
        skipIds={useExceptIds ? undefined : legacyExceptCase ? unselectedIds : currentSelectedIds}
        exceptIds={useExceptIds ? currentSelectedIds : undefined}
        affectedCount={exceptCase ? exceptCount : currentSelectedIds.length}
        exceptSelected={exceptCase ? currentSelectedIds.length : null}
        onError={errCtx?.setError}
        onSuccess={onSkipOrResume}
      />,
    );
  };

  return (
    <>
      <div className={styles['runpayroll-skip-button']}>
        <StandardButton
          onClick={() => showConfirm(isAllSelectedSkipped ? SkipTypes.RESUME : SkipTypes.SKIPPED)}>
          {`${isAllSelectedSkipped ? 'RESUME' : 'SKIP'} SELECTED (${selectedIds.value.length})`}
        </StandardButton>
        {showDropdown && (
          <StandardButton onClick={toggleDropdown}>
            <img
              className={`inline-block transition duration-300 ${
                isOptionsOpened ? 'transform rotate-180 ' : ''
              }`}
              src={getStaticMediaUrl(arrowIcon)}
              width="12px"
            />
          </StandardButton>
        )}
        {isOptionsOpened && (
          <div className={styles['runpayroll-skip-options']}>
            {!isAllSelectedSkipped && !isAllSelectedUnskipped && (
              <div className={styles['runpayroll-skip-option']}  onClick={() => showConfirm(SkipTypes.RESUME)}>
                {`Resume Selected (${selectedIds.value.length})`}
              </div>
            )}
            {canSkipAllExcept && (
              <div className={styles['runpayroll-skip-option']} onClick={() => showConfirm(SkipTypes.SKIPPED, true)}>
                {`Skip All Except Selected (${exceptCount})`}
              </div>
            )}
            {canResumeAllExcept && (
              <div className={styles['runpayroll-skip-option']} onClick={() => showConfirm(SkipTypes.RESUME, true)}>
                {`Resume All Except Selected (${exceptCount})`}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SkipButton;
